import React, { useState, useRef, useEffect } from "react";
import MeditationType from "./MeditationType";
import SymptomSelector from "./SymptomSelector";
import {
  emotionalSymptoms,
  meditationTypes,
} from "../../data/meditationSymptoms";

const getTimestamp = () => new Date().toISOString();

const allMovementTypes = [
  "Gentle Yoga",
  "Stretching",
  "Mobility Work",
  "Foam Rolling",
  "Tai Chi",
  "Qi Gong",
  "Breathwork / Somatic Movement",
  "Functional Movement",
  "Dancing",
  "Rebounding (Mini Trampoline)",
  "Gardening",
  "Cleaning / Tidying with Music",
  "Playing with Kids / Grandkids",
  "Nature Walk",
  "Walking the Dog",
  "Swimming",
  "Sports (tennis, soccer, basketball, pickleball)",
  "Strength Training",
  "Bodyweight Circuit",
  "HIIT (High-Intensity Interval Training)",
  "Running / Jogging",
  "Hiking",
  "Biking / Cycling",
  "Barre",
  "Pilates",
  "Sprint Intervals",
  "CrossFit / Bootcamp",
  "Climbing Stairs",
  "Resistance Bands",
];

const Movement = ({ logEntry, onChange, isVisible }) => {
  if (!isVisible) {
    return null;
  }

  const [movementTypeInput, setMovementTypeInput] = useState("");
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [addingCustomType, setAddingCustomType] = useState(false);
  const [customMovementType, setCustomMovementType] = useState("");
  const dropdownRef = useRef(null);
  const selectedTypes = logEntry.types || [];
  const filteredMovementTypes = allMovementTypes.filter(
    (type) =>
      !selectedTypes.includes(type) &&
      (!movementTypeInput ||
        type.toLowerCase().includes(movementTypeInput.toLowerCase()))
  );

  useEffect(() => {
    if (!showTypeDropdown) return;
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowTypeDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTypeDropdown]);

  const handleChange = (field, value) => {
    onChange({
      ...logEntry,
      [field]: value,
      Timestamp: logEntry.Timestamp || getTimestamp(),
    });
  };

  const getWarningsFromDetails = (details) => {
    if (!details || typeof details !== "string") return [];
    const text = details.toLowerCase();
    return Triggers.filter((entry) =>
      entry.sources?.some((keyword) => {
        if (!text.includes(keyword)) return false;
        // If this entry has exclusions, ensure none are present
        if (entry.exclusions) {
          return !entry.exclusions.some((ex) => text.includes(ex));
        }
        return true;
      })
    );
  };

  const effectiveDuration = logEntry.duration == null ? 15 : logEntry.duration;

  const handleAddType = (type) => {
    if (!type || selectedTypes.includes(type)) return;
    const newTypes = [...selectedTypes, type];
    handleChange("types", newTypes);
    setMovementTypeInput("");
    setShowTypeDropdown(false);
    setAddingCustomType(false);
    setCustomMovementType("");
  };

  const handleRemoveType = (type) => {
    const newTypes = selectedTypes.filter((t) => t !== type);
    handleChange("types", newTypes);
  };

  return (
    <div className="tw-px-4 tw-mb-2">
      {/* Energy Level */}
      <div className="tw-mb-4">
        <label className="tw-block tw-font-medium tw-mb-1">Energy Level</label>
        <div className="tw-flex tw-items-center tw-gap-4">
          <input
            type="range"
            min={1}
            max={10}
            value={logEntry.energy || 5}
            onChange={(e) => handleChange("energy", Number(e.target.value))}
            className="tw-w-full"
          />
          <span className="tw-text-lg tw-font-semibold tw-w-8 tw-text-center">
            {logEntry.energy || 5}
          </span>
        </div>
      </div>

      {/* Movement Mood */}
      <div className="tw-mb-4">
        <label className="tw-block tw-font-medium tw-mb-1">Movement Mood</label>
        <div className="tw-relative tw-mb-2">
          <select
            value={logEntry.mood || ""}
            onChange={(e) => handleChange("mood", e.target.value)}
            className="tw-w-full tw-border tw-rounded-lg tw-p-4 dark:tw-bg-black"
            style={{
              WebkitAppearance: "none",
              MozAppearance: "none",
              appearance: "none",
            }}
          >
            <option value="">Select mood</option>
            <option value="Recovery">Recovery</option>
            <option value="Fun">Fun</option>
            <option value="Challenge">Challenge</option>
          </select>
          <div className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pr-4">
            <svg
              className="tw-w-4 tw-h-4 tw-text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Movement Type */}
      <div className="tw-mb-4">
        <label className="tw-block tw-font-medium tw-mb-1">
          Movement Type(s)
        </label>
        <div className="tw-flex tw-gap-2 tw-mb-2">
          <div className="tw-relative tw-w-full" ref={dropdownRef}>
            <input
              type="text"
              placeholder="Search or select movement types"
              value={movementTypeInput || ""}
              onChange={(e) => {
                setMovementTypeInput(e.target.value);
                setShowTypeDropdown(true);
                setAddingCustomType(false);
              }}
              onFocus={() => {
                setShowTypeDropdown(true);
                setAddingCustomType(false);
              }}
              className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-4 tw-py-3 tw-w-full dark:tw-bg-black"
            />
            {showTypeDropdown && (
              <div className="tw-absolute tw-z-10 tw-w-full tw-bg-white dark:tw-bg-black tw-border tw-border-gray-300 tw-rounded-xl tw-overflow-hidden tw-flex tw-flex-col tw-max-h-80">
                <div className="tw-overflow-auto tw-max-h-80">
                  {filteredMovementTypes.map((option) => (
                    <div
                      key={option}
                      className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700"
                      onClick={() => handleAddType(option)}
                    >
                      {option}
                    </div>
                  ))}
                </div>
                <div className="tw-sticky tw-bottom-0 tw-bg-white dark:tw-bg-black tw-border-t tw-border-gray-200 tw-px-4 tw-py-3">
                  <button
                    className="tw-text-blue-800 tw-text-base"
                    onClick={() => {
                      setAddingCustomType(true);
                      setShowTypeDropdown(false);
                    }}
                  >
                    + <span className="hover:tw-underline">Add your own</span>
                  </button>
                </div>
              </div>
            )}
            {movementTypeInput && (
              <button
                type="button"
                onClick={() => setMovementTypeInput("")}
                className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pr-2 hover:tw-text-gray-600"
                aria-label="Clear input"
              >
                ×
              </button>
            )}
          </div>
        </div>
        {addingCustomType && (
          <div className="tw-flex tw-gap-2 tw-mb-2">
            <input
              type="text"
              className="tw-border tw-rounded-lg tw-p-4 tw-w-full"
              placeholder="Enter custom movement type"
              value={customMovementType}
              onChange={(e) => setCustomMovementType(e.target.value)}
            />
            <button
              type="button"
              onClick={() =>
                customMovementType.trim() &&
                handleAddType(customMovementType.trim())
              }
              className={`tw-px-5 tw-rounded-lg tw-min-h-[50px] tw-text-sm ${
                customMovementType.trim()
                  ? "tw-bg-green-500 tw-text-white hover:tw-bg-green-600"
                  : "tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed"
              }`}
              disabled={!customMovementType.trim()}
            >
              Add
            </button>
          </div>
        )}
        {selectedTypes.length > 0 && (
          <div className="tw-mt-3 tw-mb-6">
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {selectedTypes.map((type) => (
                <div
                  key={type}
                  className="tw-flex tw-items-center tw-bg-black tw-text-white tw-text-sm lg:tw-text-base tw-px-3 lg:tw-px-5 tw-py-2 tw-rounded-full"
                >
                  {type}
                  <button
                    type="button"
                    onClick={() => handleRemoveType(type)}
                    className="tw-ml-2 tw-text-white hover:tw-text-gray-300"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Duration */}
      <div className="tw-mb-4">
        <label className="tw-block tw-font-medium tw-mb-1">
          Duration (minutes)
        </label>
        <div className="tw-flex tw-gap-2 tw-mb-2 tw-flex-wrap">
          {[15, 20, 30, 45, 60].map((min) => (
            <button
              key={min}
              type="button"
              onClick={() => handleChange("duration", min)}
              className={`tw-px-4 tw-py-2 tw-rounded-full tw-border tw-text-sm tw-font-medium ${
                effectiveDuration === min
                  ? "tw-bg-black tw-text-white"
                  : "tw-bg-gray-100 tw-text-gray-700"
              }`}
            >
              {min} min
            </button>
          ))}
          <button
            type="button"
            onClick={() =>
              handleChange(
                "duration",
                effectiveDuration &&
                  ![15, 20, 30, 45, 60].includes(effectiveDuration)
                  ? effectiveDuration
                  : ""
              )
            }
            className={`tw-px-4 tw-py-2 tw-rounded-full tw-border tw-text-sm tw-font-medium ${
              ![15, 20, 30, 45, 60].includes(effectiveDuration)
                ? "tw-bg-black tw-text-white"
                : "tw-bg-gray-100 tw-text-gray-700"
            }`}
          >
            Other
          </button>
        </div>
        {/* Show custom input if not a preset */}
        {![15, 20, 30, 45, 60].includes(effectiveDuration) && (
          <input
            type="number"
            min={1}
            max={120}
            value={effectiveDuration || ""}
            onChange={(e) => handleChange("duration", Number(e.target.value))}
            className="tw-w-full tw-border tw-rounded-lg tw-p-4 dark:tw-bg-black"
            placeholder="Enter custom duration"
          />
        )}
      </div>

      <div className="tw-mb-6">
        <div className="tw-w-full">
          <label className="tw-block tw-mb-2 tw-w-full">
            Post-Movement Feeling{" "}
          </label>
        </div>
        <textarea
          value={logEntry["Post-Movement Feeling"] || ""}
          onChange={(e) =>
            handleChange("Post-Movement Feeling", e.target.value)
          }
          className="tw-w-full tw-border tw-rounded-lg tw-p-4 dark:tw-bg-black"
          rows={2}
        />
      </div>
    </div>
  );
};

export default Movement;
